/**
 * PriceRow — кирпичи таблиц прайса (screen-18): ячейка .pcell (cellBtn/ovrCell
 * эталона js:62-65,155-166), поповер-меню long-press (.opmenu, showOvMenu
 * js:145-150) и справка-«?» (.tipbox, showTip js:373-383).
 *
 * PriceCell: A39 — ноль живой, но приглушён (.zero); .inh — наследование
 * override-строки услуг; .saved — зелёный флэш lastK (savedN в key хоста
 * перезапускает CSS-анимацию); long-press 480мс (только при onLp — у override
 * с собственным значением) → меню «Сбросить к сетевой», click после
 * long-press гасится (гард long эталона js:160-165).
 *
 * PopMenu/TipBox — порталы в body (панель скроллится, паттерн NumPadPop):
 * place по вьюпорту — вниз от якоря +4, кламп left 6; закрытие
 * pointerdown-capture мимо; появление rAF → .on. TipBox не закрывается
 * тапом по [data-qmark] — toggle решает сам вопросик (иначе capture-close
 * съедал бы повторный клик до onClick).
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { grp } from "../../hooks/useNumPadInput";
import { FLAB, type PriceField } from "../../hooks/usePricesAdmin";
import css from "./prices.module.css";

const LP_MS = 480;
/** Разряды NBSP (fmt эталона js:5-6) — тем же NB, что превью NumPadPop. */
export const fmtNum = (n: number): string => grp(String(n));

/** Конфиг NumPadPop у ячейки (openCell js:59-60): int для «чел», money далее. */
export interface PopCfg {
  anchor: HTMLElement;
  label: string;
  mode: "money" | "int";
  unit?: string;
  max: number;
  value: number;
  onCommit: (v: number) => void;
}
export const cellPop = (
  name: string, f: PriceField, value: number, anchor: HTMLElement, onCommit: (v: number) => void,
): PopCfg => ({
  anchor, value, onCommit,
  label: `${name.slice(0, 22)} · ${FLAB[f]}`,
  mode: f === "pn" ? "int" : "money",
  unit: f === "pn" ? "чел" : undefined,
  max: f === "pn" ? 30 : 200000,
});

export interface PriceCellProps {
  val: number;
  mn?: boolean;
  inh?: boolean;
  saved?: boolean;
  onOpen: (anchor: HTMLElement) => void;
  /** Long-press 480мс (сброс override); click после него гасится. */
  onLp?: (anchor: HTMLElement) => void;
}

export function PriceCell({ val, mn, inh, saved, onOpen, onLp }: PriceCellProps) {
  const lpT = useRef(0);
  const longRef = useRef(false);
  const clear = () => window.clearTimeout(lpT.current);
  useEffect(() => clear, []);
  return (
    <button
      type="button"
      className={`${css.pcell} ${val ? "" : css.zero} ${mn ? css.min : ""} ${inh ? css.inh : ""} ${saved ? css.saved : ""}`}
      onPointerDown={(e) => {
        longRef.current = false;
        if (!onLp) return;
        const a = e.currentTarget;
        clear();
        lpT.current = window.setTimeout(() => { longRef.current = true; onLp(a); }, LP_MS);
      }}
      onPointerUp={clear}
      onPointerLeave={clear}
      onPointerCancel={clear}
      onClick={(e) => {
        if (longRef.current) { longRef.current = false; return; }
        onOpen(e.currentTarget);
      }}
    >
      <span className={css.num}>{fmtNum(val)}</span>
    </button>
  );
}

/** Позиционирование поповера у якоря: вниз +4 → вверх → кламп (вьюпорт). */
function place(el: HTMLElement, anchor: HTMLElement) {
  const ar = anchor.getBoundingClientRect();
  el.style.left = `${Math.max(6, Math.min(ar.left, window.innerWidth - el.offsetWidth - 6))}px`;
  el.style.top = el.offsetHeight <= window.innerHeight - ar.bottom - 8
    ? `${ar.bottom + 4}px`
    : `${Math.max(6, ar.top - el.offsetHeight - 4)}px`;
}

function usePopover(onClose: () => void, skipSel?: string) {
  const ref = useRef<HTMLDivElement>(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setOn(true));
    const down = (e: globalThis.PointerEvent) => {
      const t = e.target;
      if (t instanceof Node && ref.current?.contains(t)) return;
      if (skipSel && t instanceof Element && t.closest(skipSel)) return;
      onClose();
    };
    const t = window.setTimeout(() => document.addEventListener("pointerdown", down, true), 0);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
      document.removeEventListener("pointerdown", down, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { ref, on };
}

/** Меню long-press у ячейки/чипа (эталон .opmenu — один красный пункт). */
export function PopMenu({ anchor, label, onPick, onClose }: {
  anchor: HTMLElement; label: string; onPick: () => void; onClose: () => void;
}) {
  const { ref, on } = usePopover(onClose);
  useLayoutEffect(() => { if (ref.current) place(ref.current, anchor); }, [anchor, ref]);
  return createPortal(
    <div ref={ref} className={`${css.opmenu} ${on ? css.on : ""}`}>
      <button type="button" onClick={() => { onClose(); onPick(); }}>{label}</button>
    </div>,
    document.body,
  );
}

/** Справка-«?» (плотность: статичные подсказки живут во всплывашке). */
export function TipBox({ anchor, onClose, children }: {
  anchor: HTMLElement; onClose: () => void; children: ReactNode;
}) {
  const { ref, on } = usePopover(onClose, "[data-qmark]");
  useLayoutEffect(() => { if (ref.current) place(ref.current, anchor); }, [anchor, ref]);
  return createPortal(
    <div ref={ref} className={`${css.tipbox} ${on ? css.on : ""}`}>{children}</div>,
    document.body,
  );
}
